import {
    CloseButton,
    CrudMode,
    Stack,
    Switch,
    Typography,
    UpdateButton,
    newToast,
    ValidationErrors,
    DropDownMenu,
    MenuItemType,
} from "@compass/core";
import { SubmitButton } from "components";
import { Lep, Permission, PermissionAction } from "generated/openApi";
import { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import { ListContext } from "@compass/core";
import ApiService from "services/apiService";

export const LEPPageHeader = (props: {
    rootPermission: Permission;
    handle: string;
    crudMode: CrudMode;
    fetch: (id: number) => void;
    reroute: (id: number, crudMode: CrudMode) => void;
    lep: Lep;
    setLep: (lep: Lep) => void;
    initLep: Lep;
    setValidationErrors: (errors: ValidationErrors) => void;
    unsavedChanges: boolean;
    resetUnsavedChanges: (items: any[]) => void;
    closeOnSave?: boolean | undefined;
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const id = props.lep.id;
    const [createAnother, setCreateAnother] = useState<boolean>(false);
    const { loadList } = useContext(ListContext);

    const save = (lep: Lep) => {
        props.setValidationErrors({});

        if (id > 0) {
            ApiService.lep
                .saveUpdate(lep)
                .then(() => {
                    loadList?.({ resetPage: true });
                    props.fetch(id);
                    dispatch(setAppToast(newToast("success")));
                    if (props.closeOnSave) navigate("../");
                    else props.reroute(id, "view");
                })
                .catch((ex) => {
                    dispatch(setAppToast(newToast("error")));
                    props.setValidationErrors(ex.data);
                });
        } else {
            ApiService.lep
                .saveCreate(lep)
                .then((response) => {
                    loadList?.({ resetPage: true });
                    const newId = response;

                    if (createAnother) {
                        props.setLep(props.initLep);
                        props.resetUnsavedChanges([props.initLep]);
                    } else {
                        props.fetch(newId);
                        if (props.closeOnSave) navigate("../");
                        else props.reroute(newId, "view");
                    }

                    dispatch(setAppToast(newToast("success")));
                })
                .catch((ex) => {
                    dispatch(setAppToast(newToast("error")));
                    props.setValidationErrors(ex.data);
                });
        }
    };

    const postDelete = (id: number) => {
        if (window.confirm("Are you sure you want to delete this LEP?")) {
            ApiService.lep.delete(id).then(() => {
                loadList?.({ resetPage: true });
                navigate("../");
                dispatch(setAppToast(newToast("success")));
            });
        }
    };

    const menuItems: MenuItemType[] = [];
    if (id > 0 && isAuthorized(props.rootPermission, PermissionAction.Delete)) {
        menuItems.push({
            label: <Typography color="error">Delete</Typography>,
            action: () => postDelete(id),
        });
    }

    return (
        <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing="sm">
                <Typography variant="inherit">
                    {id === 0 ? "New" : ""} {props.handle}
                </Typography>
                {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
            </Stack>

            <Stack direction="row" alignItems="center" spacing="sm">
                {id === 0 && (
                    <Switch
                        value={createAnother}
                        onChange={(e) => setCreateAnother(e.target.checked)}
                        label="+ more"
                    />
                )}

                <CloseButton theme="Dark" confirm={props.unsavedChanges} />

                {props.crudMode === "view" &&
                    isAuthorized(props.rootPermission, PermissionAction.Update) && (
                        <UpdateButton theme="Dark" onClick={() => props.reroute(id, "edit")} />
                    )}

                {props.crudMode === "edit" && (
                    <SubmitButton
                        theme="Dark"
                        onClick={() => save(props.lep)}
                        disabled={!props.unsavedChanges}
                    />
                )}
            </Stack>
        </Stack>
    );
};